import { AppLayout, PageBackLink } from '../../../components/layout';
import { LeaveActionToast } from '../../leave-management/components/LeaveActionToast';
import '../inventory.css';
import {
  fetchProductById,
  fetchVendorDropdownOptionsForProduct,
} from '../../../services/productCatalogService';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import i18n from '../../../i18n';
import { Trans } from 'react-i18next';

const TOAST_KEY = 'product_catalog_toast';

function translateProductStatus(status) {
  if (status === 'Active') return i18n.t('status.active');
  if (status === 'Inactive') return i18n.t('status.inactive');
  if (status === 'End of Production') return i18n.t('inventory.endOfProduction');
  return status;
}

function resolveVendorName(product, vendors, which) {
  const idKey = which === 1 ? 'preferredVendor1Id' : 'preferredVendor2Id';
  const nameKey = which === 1 ? 'preferredVendor1' : 'preferredVendor2';
  if (product[nameKey]) return product[nameKey];
  const id = product[idKey];
  if (!id) return null;
  const match = vendors.find((v) => String(v.id) === String(id));
  return match ? match.tradeName || match.name || match.label : null;
}

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const raw = sessionStorage.getItem(TOAST_KEY);
    if (!raw) return;
    try {
      setToast(JSON.parse(raw));
    } catch {
      setToast(null);
    }
    sessionStorage.removeItem(TOAST_KEY);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProductById(productId);
        if (cancelled) return;
        if (!data) {
          setProduct(null);
          setError(i18n.t('validation.productNotFound'));
        } else {
          setProduct(data);
        }
      } catch (err) {
        if (!cancelled) {
          setProduct(null);
          setError(err?.message || i18n.t('inventory.failedToLoadProduct'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  // Resolve preferred vendor names from GET /product-vendors/search
  useEffect(() => {
    let cancelled = false;
    fetchVendorDropdownOptionsForProduct({ size: 100 })
      .then((list) => {
        if (!cancelled) setVendors(list || []);
      })
      .catch(() => {
        if (!cancelled) setVendors([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onEdit = () => navigate(`/IMS/inventory/products/${productId}/edit`);
  const onBackToList = () => navigate('/IMS/inventory/products');

  if (loading) {
    return (
      <AppLayout>
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: 'var(--text-grey)', fontSize: '14px' }}><Trans i18nKey="inventory.loadingProduct" /></p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!product) {
    return (
      <AppLayout>
        <div className="inventory-module">
          <div style={{ padding: '40px', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary-navy)' }}>
              {error || i18n.t('inventory.noProductSelected')}
            </h3>
            <button type="button" className="btn-secondary" style={{ marginTop: '16px' }} onClick={onBackToList}><Trans i18nKey="inventory.backToCatalog" /></button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const isEndOfProd = product.status === 'End of Production';
  const vendor1 = resolveVendorName(product, vendors, 1);
  const vendor2 = resolveVendorName(product, vendors, 2);
  const notSet = i18n.t('inventory.notSet');

  return (
    <AppLayout overlay={<LeaveActionToast toast={toast} />}>
      <div className="inventory-module">
    <div style={{ padding: '0 8px' }}>
      
      <PageBackLink to="/IMS/inventory/products" />

      <div className="page-header-responsive" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: '0' }}>{product.name}</h1>
            <span style={{ 
              fontSize: '11px', 
              fontWeight: '700', 
              padding: '3px 8px', 
              borderRadius: '12px', 
              backgroundColor: product.status === 'Active' ? '#d1fae5' : isEndOfProd ? '#fef3c7' : '#f1f5f9', 
              color: product.status === 'Active' ? '#065f46' : isEndOfProd ? '#b45309' : '#475569',
              textTransform: 'capitalize'
            }}>
              {translateProductStatus(product.status)}
            </span>
          </div>
          <div style={{ fontSize: '14px', color: 'var(--text-grey)', marginTop: '4px', fontFamily: 'monospace', fontWeight: '600' }}>
            {product.partNumber}
          </div>
        </div>

        <button 
          type="button" 
          onClick={onEdit}
          style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              height: '40px',
              padding: '0 18px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'var(--primary-navy)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'var(--transition-all)',
              boxShadow: 'var(--shadow-sm)',
          }}
        >
          <Trans i18nKey="common.edit" />
        </button>
      </div>

      {isEndOfProd && (
        <div style={{ display: 'flex', gap: '12px', padding: '16px', backgroundColor: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '12px', color: '#b45309', marginBottom: '24px', alignItems: 'flex-start' }}>
          <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h4 style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '700' }}><Trans i18nKey="inventory.productStatusEndOfProductionSpares" /></h4>
            <p style={{ margin: 0, fontSize: '12px', lineHeight: '1.45', opacity: 0.95 }}>
              <Trans i18nKey="inventory.endOfProductionDetailHint" />
            </p>
          </div>
        </div>
      )}

      <div className="detail-card-container" style={{ backgroundColor: '#ffffff', padding: '28px 32px', borderRadius: '16px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)', marginBottom: '24px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--primary-navy)', margin: '0 0 24px 0' }}><Trans i18nKey="inventory.productMaster" /></h3>
        
        <div className="grid-4-cols">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="common.category" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{product.category || notSet}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.hsnCode" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', fontFamily: 'monospace' }}>{product.hsnCode || notSet}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.unitRate" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>
                {product.unitRate !== undefined && product.unitRate !== null ? `₹${Number(product.unitRate).toLocaleString('en-IN')}` : notSet}
              </span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.preferredVendor1" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{vendor1 || notSet}</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="crm.manufacturer" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{product.manufacturer || notSet}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.moq" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{product.moq !== undefined && product.moq !== null ? product.moq : notSet}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.preferredVendor2" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{vendor2 || notSet}</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="productMetrics.unitOfMeasure" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{product.unitOfMeasure || notSet}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.reorderQuantity" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{product.reorderQuantity !== undefined && product.reorderQuantity !== null ? product.reorderQuantity : notSet}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.abcCategory" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{product.abcCategory || notSet}</span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.listPrice" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>₹{(product.listPrice || 0).toLocaleString('en-IN')}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="inventory.maintenanceStockQty" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{product.maintenanceStockQuantity !== undefined && product.maintenanceStockQuantity !== null ? product.maintenanceStockQuantity : notSet}</span>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="status.status" /></span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)' }}>{product.status ? translateProductStatus(product.status) : notSet}</span>
            </div>
          </div>
        </div>

        {product.description && (
          <div style={{ marginTop: '24px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}><Trans i18nKey="common.description" /></span>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-grey)', lineHeight: '1.5' }}>{product.description}</p>
          </div>
        )}
      </div>

    </div>
      </div>
    </AppLayout>
  );
}
